/**
 * @module pages/Zeiterfassung
 * @description Zeiterfassung Phase 1 MVP (#989, v2.426.0).
 *
 * 3 Tabs:
 *  - Mein Tag: aktiver Timer + Heute-Liste + Quick-Add + Wochenansicht-Toggle
 *  - Mein Monat: Aggregat pro Kunde + Kategorie + Monat-Wechsler
 *  - Auswertung: Admin-only, alle MA, CSV-Export
 *
 * @since v2.426.0
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Play, Square, Plus, Edit2, Trash2, Download, ChevronLeft, ChevronRight, Filter, X, Clock, Pause, AlertTriangle } from 'lucide-react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useDialog } from '../hooks/useDialog';
import SearchableSelect from '../components/SearchableSelect';

const API = '';

const ADMIN_ROLES = new Set(['administrator', 'admin', 'ops_manager', 'manager', 'cs_teamlead', 'controlling']);

// #1008 — Persistenter Scope-Toggle (Nur ich / Team) pro Tab via localStorage
function loadScope(key, fallback = 'mein') {
  try {
    const v = localStorage.getItem(key);
    return v === 'team' ? 'team' : (v === 'mein' ? 'mein' : fallback);
  } catch { return fallback; }
}
function saveScope(key, val) {
  try { localStorage.setItem(key, val); } catch { /* SSR/quota */ }
}
function loadMaFilter(key) {
  try { return localStorage.getItem(key) || ''; } catch { return ''; }
}
function saveMaFilter(key, val) {
  try { val ? localStorage.setItem(key, val) : localStorage.removeItem(key); } catch { /* SSR/quota */ }
}

function formatMin(m) {
  if (!m && m !== 0) return '—';
  const mins = Math.round(m);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  if (r === 0) return `${h} h`;
  return `${h} h ${r} min`;
}

function formatHours(min) {
  if (!min && min !== 0) return '—';
  return (min / 60).toFixed(2).replace('.', ',') + ' h';
}

function pad2(n) { return n < 10 ? `0${n}` : `${n}`; }

function formatTimerSec(sec) {
  if (sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

// #1000 Multi-Timer-Sicht — gleiche Logik wie TimerFloatingPanel für Konsistenz.
function computeElapsedSec(timer, nowMs) {
  if (!timer || !timer.start_at) return 0;
  const startMs = new Date(timer.start_at).getTime();
  const accPauseMs = (parseInt(timer.accumulated_pause_min, 10) || 0) * 60000;
  const refMs = timer.paused_at ? new Date(timer.paused_at).getTime() : nowMs;
  return Math.max(0, Math.floor((refMs - startMs - accPauseMs) / 1000));
}
function pausedSinceMin(timer) {
  if (!timer?.paused_at) return null;
  const ms = Date.now() - new Date(timer.paused_at).getTime();
  return Math.max(0, Math.floor(ms / 60000));
}
function formatHm(min) {
  if (!min || min < 0) min = 0;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function isoDay(d) {
  return d.toISOString().slice(0, 10);
}

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay() === 0 ? 7 : x.getDay();
  x.setDate(x.getDate() - day + 1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfMonth(year, month0) { return new Date(year, month0, 1); }
function endOfMonth(year, month0) { return new Date(year, month0 + 1, 1); }

function fmtDateTime(s) {
  if (!s) return '—';
  const d = new Date(s);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}. ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function fmtTime(s) {
  if (!s) return '—';
  const d = new Date(s);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export default function Zeiterfassung() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const dialog = useDialog();
  const [activeTab, setActiveTab] = useTabParam('tag');

  const isAdmin = ADMIN_ROLES.has(user?.rolle) && user?.permissions?.zeiterfassung === 'full';
  const ownMaId = user?.mitarbeiter_id || null;

  const [kategorien, setKategorien] = useState([]);
  const [kunden, setKunden] = useState([]);
  const [mitarbeiter, setMitarbeiter] = useState([]);

  useEffect(() => {
    fetch('/api/zeiterfassung/kategorien', { credentials: 'include' })
      .then(r => r.json()).then(d => setKategorien(d.kategorien || [])).catch(() => {});
    fetch('/api/kunden', { credentials: 'include' })
      .then(r => r.json()).then(d => setKunden(Array.isArray(d) ? d : [])).catch(() => {});
    // #1008 — Mitarbeiter-Liste für Team-Filter (nur aktive)
    fetch('/api/mitarbeiter', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.mitarbeiter || []);
        setMitarbeiter(list.filter(m => m.aktiv !== false));
      })
      .catch(() => setMitarbeiter([]));
  }, []);

  const reloadKategorien = useCallback(() => {
    fetch('/api/zeiterfassung/kategorien', { credentials: 'include' })
      .then(r => r.json()).then(d => setKategorien(d.kategorien || [])).catch(() => {});
  }, []);

  // #991 Compliance-Banner: offene Warnungen für aktuellen User
  const [complianceWarnings, setComplianceWarnings] = useState([]);
  const reloadCompliance = useCallback(() => {
    fetch('/api/zeiterfassung/compliance/banner', { credentials: 'include' })
      .then(r => r.ok ? r.json() : { warnings: [] })
      .then(d => setComplianceWarnings(d.warnings || []))
      .catch(() => setComplianceWarnings([]));
  }, []);
  useEffect(() => { reloadCompliance(); }, [reloadCompliance]);
  const dismissComplianceWarning = useCallback(async (id) => {
    await fetch(`/api/zeiterfassung/compliance/warnings/${id}/status`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'erledigt' }),
    });
    reloadCompliance();
  }, [reloadCompliance]);

  const tabs = [
    { key: 'tag', label: t('zeiterfassung.tabs.tag') || 'Mein Tag' },
    { key: 'monat', label: t('zeiterfassung.tabs.monat') || 'Mein Monat' },
  ];
  if (isAdmin) tabs.push({ key: 'auswertung', label: t('zeiterfassung.tabs.auswertung') || 'Auswertung' });
  if (isAdmin) tabs.push({ key: 'einstellungen', label: t('zeiterfassung.tabs.einstellungen') || 'Einstellungen' });

  return (
    <div className="px-6 py-6 w-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[22px] font-bold text-[var(--text-primary)]">{t('zeiterfassung.title') || 'Zeiterfassung'}</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">{t('zeiterfassung.subtitle') || 'Tätigkeiten erfassen, Zeit pro Kunde dokumentieren'}</p>
        </div>
      </div>

      {complianceWarnings.length > 0 && (
        <div className="mb-4 rounded-[10px] border border-[#F59E0B] bg-[#FEF3C7] dark:bg-[#3F2A0B] dark:border-[#C97A0E] p-3 flex items-start gap-3">
          <div className="flex-1">
            <div className="font-semibold text-[14px] text-[#7C2D12] dark:text-[#FCD34D] mb-1">
              {t('zeiterfassung.compliance.bannerTitle') || 'Zeiterfassung unvollständig'}
            </div>
            <div className="text-[13px] text-[#7C2D12] dark:text-[#FCD34D]">
              {(t('zeiterfassung.compliance.bannerHint') || 'Bitte ergänze deine fehlenden Einträge:')}
              <span className="ml-1">
                {complianceWarnings.slice(0, 5).map((w, i) => (
                  <span key={w.id} className="inline-flex items-center gap-1 mr-2">
                    <strong>{w.datum}</strong> ({Math.round((w.dokumentiert_min/60)*10)/10}h von {Math.round((w.ist_min/60)*10)/10}h, {Math.round(w.prozent)}%)
                    <button
                      type="button"
                      onClick={() => dismissComplianceWarning(w.id)}
                      className="text-[11px] underline hover:no-underline"
                      title={t('zeiterfassung.compliance.markDone') || 'Als erledigt markieren'}
                    >×</button>
                    {i < Math.min(4, complianceWarnings.length - 1) && '·'}
                  </span>
                ))}
                {complianceWarnings.length > 5 && (
                  <span> +{complianceWarnings.length - 5} {t('zeiterfassung.compliance.more') || 'weitere'}</span>
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-[2px] border border-[var(--border-card,#E5E7EB)] mb-6">
        {tabs.map(tb => (
          <button
            key={tb.key}
            onClick={() => setActiveTab(tb.key)}
            className={`px-4 py-[6px] rounded-[7px] text-[13px] transition-colors ${
              activeTab === tb.key
                ? 'bg-[var(--bg-card,#fff)] text-[var(--text-primary)] font-semibold shadow-sm'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {activeTab === 'tag' && <MeinTagTab kategorien={kategorien} kunden={kunden} mitarbeiter={mitarbeiter} ownMaId={ownMaId} isAdmin={isAdmin} t={t} dialog={dialog} />}
      {activeTab === 'monat' && <MeinMonatTab kategorien={kategorien} kunden={kunden} mitarbeiter={mitarbeiter} ownMaId={ownMaId} isAdmin={isAdmin} t={t} />}
      {activeTab === 'auswertung' && isAdmin && <AuswertungTab kategorien={kategorien} kunden={kunden} t={t} />}
      {activeTab === 'einstellungen' && isAdmin && <EinstellungenTab t={t} dialog={dialog} onCategoriesChanged={reloadKategorien} />}

      {dialog.element}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Mein Tag
// ═══════════════════════════════════════════════════════════════

function MeinTagTab({ kategorien, kunden, mitarbeiter, ownMaId, isAdmin, t, dialog }) {
  // #1000 Multi-Timer-Sicht: array statt single, /timer/offene statt /timer/active
  const [offene, setOffene] = useState([]);
  const [eintraege, setEintraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [stopTimer, setStopTimer] = useState(null); // Timer-Objekt das gestoppt werden soll
  const [busyId, setBusyId] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [view, setView] = useState('tag'); // 'tag' | 'woche'
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [now, setNow] = useState(Date.now());

  // #1008 — Scope-Toggle (Nur ich / Team), persistiert pro Tab
  const [scope, setScopeState] = useState(() => loadScope('zeiterfassung.scope.tag'));
  const [filterMa, setFilterMaState] = useState(() => loadMaFilter('zeiterfassung.filterMa.tag'));
  const setScope = (v) => { setScopeState(v); saveScope('zeiterfassung.scope.tag', v); };
  const setFilterMa = (v) => { setFilterMaState(v); saveMaFilter('zeiterfassung.filterMa.tag', v); };

  // Timer-Tick — nur wenn ein running-Timer existiert
  const hasRunning = offene.some(o => !o.paused_at);
  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasRunning]);

  const fetchOffene = useCallback(async () => {
    try {
      const r = await fetch('/api/zeiterfassung/timer/offene', { credentials: 'include' });
      if (!r.ok) { setOffene([]); return; }
      const d = await r.json();
      setOffene(d.offene || []);
    } catch { setOffene([]); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await fetchOffene();

      let from, to;
      if (view === 'woche') {
        const sow = weekStart;
        const eow = new Date(sow); eow.setDate(eow.getDate() + 7);
        from = sow.toISOString();
        to = eow.toISOString();
      } else {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const tom = new Date(today); tom.setDate(tom.getDate() + 1);
        from = today.toISOString();
        to = tom.toISOString();
      }
      const params = new URLSearchParams({ von: from, bis: to, limit: '200', scope });
      if (scope === 'team' && filterMa) params.set('mitarbeiter_id', filterMa);
      const eRes = await fetch(`/api/zeiterfassung/eintraege?${params}`, { credentials: 'include' }).then(r => r.json());
      setEintraege(eRes.eintraege || []);
    } finally {
      setLoading(false);
    }
  }, [view, weekStart, fetchOffene, scope, filterMa]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // #1000 State-Sync: 10s-Polling der offenen Timer (Konsistenz mit Floating Panel) +
  // Custom-Event-Listener `weship:timer-changed` (Aktion in einer Sicht aktualisiert die andere).
  useEffect(() => {
    const iv = setInterval(fetchOffene, 10000);
    const onChanged = () => fetchOffene();
    window.addEventListener('weship:timer-changed', onChanged);
    return () => { clearInterval(iv); window.removeEventListener('weship:timer-changed', onChanged); };
  }, [fetchOffene]);

  function emitTimerChanged() {
    try { window.dispatchEvent(new CustomEvent('weship:timer-changed')); } catch (_) { /* SSR/old browser */ }
  }

  const stoppedEintraege = useMemo(() => eintraege.filter(e => e.end_at), [eintraege]);
  const tagSumme = useMemo(() => stoppedEintraege.reduce((acc, e) => acc + (e.dauer_min || 0), 0), [stoppedEintraege]);
  const billableSumme = useMemo(() => stoppedEintraege.filter(e => e.billable).reduce((acc, e) => acc + (e.dauer_min || 0), 0), [stoppedEintraege]);

  // Sortierung: zuerst running, dann pausierte (jüngste Pause zuerst — paused_at DESC)
  const sortedOffene = useMemo(() => {
    const arr = [...offene];
    arr.sort((a, b) => {
      const aRun = !a.paused_at, bRun = !b.paused_at;
      if (aRun && !bRun) return -1;
      if (!aRun && bRun) return 1;
      if (aRun && bRun) return new Date(b.start_at).getTime() - new Date(a.start_at).getTime();
      return new Date(b.paused_at).getTime() - new Date(a.paused_at).getTime();
    });
    return arr;
  }, [offene]);
  const runningTimer = sortedOffene.find(o => !o.paused_at) || null;
  const pausedTimers = sortedOffene.filter(o => o.paused_at);

  async function handleStartFromActiveTimer(payload) {
    const res = await fetch('/api/zeiterfassung/timer/start', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) {
      await dialog.alert(t('common.error') || 'Fehler', data.error || 'Timer konnte nicht gestartet werden', { variant: 'danger' });
      return;
    }
    emitTimerChanged();
    await loadAll();
    setShowQuickAdd(false);
  }

  async function handleStop(payload) {
    const res = await fetch('/api/zeiterfassung/timer/stop', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      await dialog.alert(t('common.error') || 'Fehler', d.error || 'Stop fehlgeschlagen', { variant: 'danger' });
      return;
    }
    emitTimerChanged();
    await loadAll();
    setStopTimer(null);
  }

  // #1000 Quick-Pause/Resume aus den Karten (kein Modal — direkter API-Call).
  async function handlePause(timerId) {
    if (busyId) return;
    setBusyId(timerId);
    try {
      const res = await fetch('/api/zeiterfassung/timer/pause', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: timerId }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        await dialog.alert(t('common.error') || 'Fehler', d.error || 'Pause fehlgeschlagen', { variant: 'danger' });
      } else {
        emitTimerChanged();
        await fetchOffene();
      }
    } finally { setBusyId(null); }
  }
  async function handleResume(timerId) {
    if (busyId) return;
    setBusyId(timerId);
    try {
      const res = await fetch('/api/zeiterfassung/timer/resume', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: timerId }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        await dialog.alert(t('common.error') || 'Fehler', d.error || 'Resume fehlgeschlagen', { variant: 'danger' });
      } else {
        emitTimerChanged();
        await fetchOffene();
      }
    } finally { setBusyId(null); }
  }

  async function handleDelete(id) {
    const ok = await dialog.confirm(
      t('zeiterfassung.deleteTitle') || 'Eintrag löschen?',
      t('zeiterfassung.deleteMessage') || 'Soll dieser Eintrag dauerhaft gelöscht werden?',
      { variant: 'danger', confirmLabel: t('common.delete') || 'Löschen' }
    );
    if (!ok) return;
    const res = await fetch(`/api/zeiterfassung/eintraege/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      await dialog.alert(t('common.error') || 'Fehler', d.error || 'Löschen fehlgeschlagen', { variant: 'danger' });
      return;
    }
    await loadAll();
  }

  return (
    <div className="space-y-4">
      {/* #1000 Multi-Timer-Sicht: Hero (running) + Kompakt-Karten (paused). Versteckt wenn 0 offene Timer. */}
      {sortedOffene.length > 0 ? (
        <ActiveTimersSection
          running={runningTimer}
          paused={pausedTimers}
          now={now}
          busyId={busyId}
          onPause={handlePause}
          onResume={handleResume}
          onStop={(timer) => setStopTimer(timer)}
          t={t}
        />
      ) : (
        <div className="rounded-[12px] border border-dashed border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] p-5 flex items-center justify-between">
          <div className="text-[13px] text-[var(--text-secondary)]">{t('zeiterfassung.keinTimer') || 'Kein aktiver Timer'}</div>
          <button onClick={() => setShowQuickAdd(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-[#338edc] text-white hover:bg-[#1f6fb5] text-[13px] font-medium">
            <Play className="w-4 h-4" />
            {t('zeiterfassung.starten') || 'Timer starten'}
          </button>
        </div>
      )}

      {/* Heute-Summe */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SumCard
          label={view === 'woche' ? (t('zeiterfassung.summeWoche') || 'Woche gesamt') : (t('zeiterfassung.summeTag') || 'Heute gesamt')}
          subtitle={scope === 'team' ? (t('zeiterfassung.scope.teamSubtitle') || 'Team gesamt') : (t('zeiterfassung.scope.meinSubtitle') || 'Persönlich')}
          value={formatHours(tagSumme)}
        />
        <SumCard
          label={t('zeiterfassung.summeBillable') || 'Davon abrechenbar'}
          subtitle={scope === 'team' ? (t('zeiterfassung.scope.teamSubtitle') || 'Team gesamt') : (t('zeiterfassung.scope.meinSubtitle') || 'Persönlich')}
          value={formatHours(billableSumme)}
          accent="#10B981"
        />
        <SumCard
          label={t('zeiterfassung.eintraege') || 'Einträge'}
          subtitle={scope === 'team' ? (t('zeiterfassung.scope.teamSubtitle') || 'Team gesamt') : (t('zeiterfassung.scope.meinSubtitle') || 'Persönlich')}
          value={`${stoppedEintraege.length}`}
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-[2px] border border-[var(--border-card,#E5E7EB)]">
          {[{ k: 'tag', l: t('zeiterfassung.heute') || 'Heute' }, { k: 'woche', l: t('zeiterfassung.woche') || 'Woche' }].map(o => (
            <button
              key={o.k}
              onClick={() => setView(o.k)}
              className={`px-3 py-[5px] rounded-[7px] text-[12px] transition-colors ${view === o.k ? 'bg-[var(--bg-card,#fff)] text-[var(--text-primary)] font-semibold shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          {scope === 'team' && (
            <SearchableSelect
              value={filterMa}
              onChange={setFilterMa}
              placeholder={t('zeiterfassung.scope.maFilterPlaceholder') || 'Mitarbeiter filtern…'}
              size="sm"
              className="min-w-[200px]"
              options={[
                { value: '', label: t('zeiterfassung.scope.alleMitarbeiter') || 'Alle Mitarbeiter' },
                ...mitarbeiter.map(m => ({ value: String(m.id), label: `${m.vorname || ''} ${m.nachname || ''}`.trim() || `MA ${m.id}` })),
              ]}
            />
          )}
          <ScopeToggle scope={scope} onChange={setScope} t={t} />
          <button onClick={() => { setEditEntry({ neu: true }); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-[13px] text-[var(--text-primary)] bg-[var(--bg-card,#fff)] hover:bg-[var(--bg-inset,#F8F9FC)]">
            <Plus className="w-4 h-4" />
            {t('zeiterfassung.manuellerEintrag') || 'Manueller Eintrag'}
          </button>
        </div>
      </div>

      {/* Liste oder Wochen-Kalender */}
      {view === 'woche' ? (
        <WochenKalender
          kategorien={kategorien}
          eintraege={stoppedEintraege}
          weekStart={weekStart}
          onPrev={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
          onNext={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}
          onToday={() => setWeekStart(startOfWeek(new Date()))}
          onEdit={(e) => setEditEntry(e)}
          t={t}
        />
      ) : (
        <EintraegeListe
          eintraege={stoppedEintraege}
          loading={loading}
          onEdit={(e) => setEditEntry(e)}
          onDelete={handleDelete}
          showDate={false}
          showMitarbeiter={scope === 'team'}
          ownMaId={ownMaId}
          isAdmin={isAdmin}
          t={t}
        />
      )}

      {showQuickAdd && (
        <TimerStartModal kategorien={kategorien} kunden={kunden} onClose={() => setShowQuickAdd(false)} onSubmit={handleStartFromActiveTimer} t={t} />
      )}
      {stopTimer && (
        <TimerStopModal
          timer={stopTimer}
          kategorien={kategorien}
          kunden={kunden}
          elapsed={computeElapsedSec(stopTimer, now)}
          onClose={() => setStopTimer(null)}
          onSubmit={(payload) => handleStop({ ...payload, id: stopTimer.id })}
          t={t}
        />
      )}
      {editEntry && (
        <EintragModal
          eintrag={editEntry.neu ? null : editEntry}
          kategorien={kategorien}
          kunden={kunden}
          onClose={() => setEditEntry(null)}
          onSaved={() => { setEditEntry(null); loadAll(); }}
          t={t}
          dialog={dialog}
        />
      )}
    </div>
  );
}

function SumCard({ label, value, accent, subtitle }) {
  return (
    <div className="rounded-[12px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
      <div className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary)] font-medium">{label}</div>
      <div className={`text-[22px] font-bold mt-1 tabular-nums`} style={{ color: accent || 'var(--text-primary)' }}>{value}</div>
      {subtitle && <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{subtitle}</div>}
    </div>
  );
}

// #1008 — Toggle Nur ich / Team
function ScopeToggle({ scope, onChange, t }) {
  const items = [
    { k: 'mein', l: t('zeiterfassung.scope.mein') || 'Nur ich' },
    { k: 'team', l: t('zeiterfassung.scope.team') || 'Team' },
  ];
  return (
    <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-[2px] border border-[var(--border-card,#E5E7EB)]" role="group" aria-label={t('zeiterfassung.scope.toggle') || 'Sicht'}>
      {items.map(o => (
        <button
          key={o.k}
          type="button"
          onClick={() => onChange(o.k)}
          className={`px-3 py-[5px] rounded-[7px] text-[12px] transition-colors ${scope === o.k ? 'bg-[var(--bg-card,#fff)] text-[var(--text-primary)] font-semibold shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  #1000 ActiveTimersSection — Multi-Timer-Sicht (Hero + Kompakt)
//  Konsistent mit TimerFloatingPanel: Status-Indikatoren, Sortierung,
//  Aktions-Buttons. State-Sync via window 'weship:timer-changed' Event.
// ═══════════════════════════════════════════════════════════════

function ActiveTimersSection({ running, paused, now, busyId, onPause, onResume, onStop, t }) {
  const total = (running ? 1 : 0) + paused.length;
  const headerText = running && paused.length > 0
    ? t('zeiterfassung.aktiveTimer.summaryBoth', { LAUFEND: 1, PAUSIERT: paused.length })
    : running
      ? t('zeiterfassung.aktiveTimer.summaryRunning', { LAUFEND: 1 })
      : t('zeiterfassung.aktiveTimer.summaryPausedOnly', { PAUSIERT: paused.length });

  return (
    <div className="rounded-[12px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] flex items-center gap-2">
        <Clock className="w-4 h-4 text-[var(--text-secondary)]" />
        <span className="text-[13px] font-semibold text-[var(--text-primary)]">
          {t('zeiterfassung.aktiveTimer.title') || 'Aktive Timer'}
        </span>
        <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#338edc] text-white text-[11px] font-bold">{total}</span>
        <span className="text-[12px] text-[var(--text-tertiary)] truncate">· {headerText}</span>
      </div>

      <div className="p-4 space-y-3">
        {running && (
          <HeroTimerCard
            timer={running}
            now={now}
            busy={busyId === running.id}
            onPause={() => onPause(running.id)}
            onStop={() => onStop(running)}
            t={t}
          />
        )}
        {paused.length > 0 && (
          <div className="space-y-2">
            {paused.map(p => (
              <CompactTimerCard
                key={p.id}
                timer={p}
                now={now}
                busy={busyId === p.id}
                onResume={() => onResume(p.id)}
                onStop={() => onStop(p)}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HeroTimerCard({ timer, now, busy, onPause, onStop, t }) {
  const elapsed = computeElapsedSec(timer, now);
  const kunde = timer.kunde_name || t('zeiterfassung.aktiveTimer.kundenlos') || 'Kein Kunde';
  return (
    <div className="rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-white dark:bg-[#1A1A2E] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="mt-1 flex-shrink-0">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
            </span>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary)] font-semibold">
              {t('zeiterfassung.aktiveTimer.timerLaeuft') || t('zeiterfassung.timerLaeuft') || 'Timer läuft'}
            </div>
            <div className="text-[28px] font-bold text-[var(--text-primary)] tabular-nums leading-tight">{formatTimerSec(elapsed)}</div>
            <div className="text-[13px] text-[var(--text-secondary)] truncate mt-0.5">
              {timer.kategorie_name || '—'} · {kunde}
            </div>
            {timer.beschreibung && (
              <div className="text-[12px] text-[var(--text-tertiary)] truncate mt-0.5" title={timer.beschreibung}>
                „{timer.beschreibung}"
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-1.5 flex-shrink-0">
          <button
            onClick={onPause}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-[13px] text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 dark:bg-amber-900/20 dark:hover:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800"
          >
            <Pause className="w-4 h-4" />
            {t('zeiterfassung.aktiveTimer.pause') || 'Pause'}
          </button>
          <button
            onClick={onStop}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] bg-[#EF4444] text-white hover:bg-[#DC2626] text-[13px] font-medium disabled:opacity-50"
          >
            <Square className="w-4 h-4" />
            {t('zeiterfassung.stoppen') || 'Stoppen'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CompactTimerCard({ timer, now, busy, onResume, onStop, t }) {
  const isZombie = !!timer.is_zombie;
  const pSince = pausedSinceMin(timer);
  const elapsed = computeElapsedSec(timer, now);
  const kunde = timer.kunde_name || t('zeiterfassung.aktiveTimer.kundenlos') || 'Kein Kunde';
  return (
    <div className={`rounded-[10px] border p-3 flex items-center gap-3 ${isZombie ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10' : 'border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] dark:bg-[#10101F]'}`}>
      <span className="flex-shrink-0">
        {isZombie ? (
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          </span>
        ) : (
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/40">
            <Pause className="w-4 h-4 text-amber-600 dark:text-amber-300" />
          </span>
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
          <span className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
            {timer.kategorie_name || '—'} · {kunde}
          </span>
          <span className="text-[11px] tabular-nums text-[var(--text-tertiary)]">
            {formatTimerSec(elapsed)} {t('zeiterfassung.aktiveTimer.aktiv') || 'aktiv'}
          </span>
        </div>
        {timer.beschreibung && (
          <div className="text-[12px] text-[var(--text-secondary)] truncate" title={timer.beschreibung}>
            „{timer.beschreibung}"
          </div>
        )}
        <div className={`text-[11px] tabular-nums mt-0.5 ${isZombie ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-[var(--text-tertiary)]'}`}>
          {isZombie
            ? t('zeiterfassung.aktiveTimer.zombieSeit', { DAYS: Math.floor((pSince || 0) / 1440) })
            : `${t('zeiterfassung.aktiveTimer.pausiertSeit') || 'Pausiert seit'} ${formatHm(pSince || 0)}`}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={onResume}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[8px] border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[12px] font-medium disabled:opacity-50 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-200 dark:hover:bg-blue-900/50"
          title={t('zeiterfassung.aktiveTimer.resume') || 'Fortsetzen'}
        >
          <Play className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{t('zeiterfassung.aktiveTimer.resume') || 'Fortsetzen'}</span>
        </button>
        <button
          onClick={onStop}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[8px] border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-[12px] font-medium disabled:opacity-50 dark:bg-red-900/30 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/50"
          title={t('zeiterfassung.stoppen') || 'Stoppen'}
        >
          <Square className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{t('zeiterfassung.stoppen') || 'Stoppen'}</span>
        </button>
      </div>
    </div>
  );
}

function EintraegeListe({ eintraege, loading, onEdit, onDelete, showDate, showMitarbeiter, ownMaId, isAdmin, t }) {
  if (loading) return <div className="text-[13px] text-[var(--text-tertiary)] py-6 text-center">{t('common.loading') || 'Lädt …'}</div>;
  if (!eintraege.length) return (
    <div className="rounded-[12px] border border-dashed border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-8 text-center text-[13px] text-[var(--text-tertiary)]">
      {t('zeiterfassung.keineEintraege') || 'Noch keine Einträge.'}
    </div>
  );

  return (
    <div className="rounded-[12px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-[var(--bg-inset,#F8F9FC)] text-[12px] text-[var(--text-tertiary)] uppercase tracking-wide font-medium">
            {showDate && <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.datum') || 'Datum'}</th>}
            {showMitarbeiter && <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.mitarbeiter') || 'Mitarbeiter'}</th>}
            <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.zeit') || 'Zeit'}</th>
            <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.kategorie') || 'Kategorie'}</th>
            <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.kunde') || 'Kunde'}</th>
            <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.beschreibung') || 'Beschreibung'}</th>
            <th className="text-right px-4 py-2.5 font-medium">{t('zeiterfassung.dauer') || 'Dauer'}</th>
            <th className="text-center px-4 py-2.5 font-medium">{t('zeiterfassung.billable') || 'Abrechnen'}</th>
            <th className="text-right px-4 py-2.5 font-medium">{t('common.actions') || 'Aktionen'}</th>
          </tr>
        </thead>
        <tbody>
          {eintraege.map(e => {
            const canMutate = !!isAdmin || (ownMaId != null && e.mitarbeiter_id === ownMaId);
            const fremdTitle = canMutate ? '' : (t('zeiterfassung.scope.fremdHint') || 'Nur eigene Einträge bearbeiten');
            return (
            <tr key={e.id} className="border-t border-[var(--border-card,#E5E7EB)]">
              {showDate && <td className="px-4 py-2.5 text-[var(--text-secondary)]">{e.start_at ? new Date(e.start_at).toLocaleDateString('de-DE') : '—'}</td>}
              {showMitarbeiter && (
                <td className="px-4 py-2.5 text-[var(--text-secondary)] whitespace-nowrap">
                  {(e.ma_vorname || e.ma_nachname) ? `${e.ma_vorname || ''} ${e.ma_nachname || ''}`.trim() : '—'}
                </td>
              )}
              <td className="px-4 py-2.5 text-[var(--text-secondary)] tabular-nums">
                {e.start_at ? `${fmtTime(e.start_at)}–${fmtTime(e.end_at)}` : '—'}
              </td>
              <td className="px-4 py-2.5 text-[var(--text-primary)]">{e.kategorie_name || '—'}</td>
              <td className="px-4 py-2.5 text-[var(--text-secondary)]">{e.kunde_name || '–'}</td>
              <td className="px-4 py-2.5 text-[var(--text-secondary)] max-w-[300px] truncate" title={e.beschreibung}>{e.beschreibung || '—'}</td>
              <td className="px-4 py-2.5 text-right text-[var(--text-primary)] font-medium tabular-nums">{formatMin(e.dauer_min)}</td>
              <td className="px-4 py-2.5 text-center">
                {e.billable ? <span className="inline-block px-2 py-0.5 rounded-[6px] bg-[#10B981]/10 text-[#10B981] text-[11px] font-medium">€</span> : <span className="text-[var(--text-tertiary)] text-[11px]">–</span>}
              </td>
              <td className="px-4 py-2.5 text-right">
                <div className="inline-flex gap-1">
                  <button
                    onClick={() => canMutate && onEdit(e)}
                    disabled={!canMutate}
                    className="p-1.5 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    title={canMutate ? (t('common.edit') || 'Bearbeiten') : fremdTitle}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => canMutate && onDelete(e.id)}
                    disabled={!canMutate}
                    className="p-1.5 rounded hover:bg-[#EF4444]/10 text-[#EF4444] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    title={canMutate ? (t('common.delete') || 'Löschen') : fremdTitle}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Mein Monat
// ═══════════════════════════════════════════════════════════════

function MeinMonatTab({ kategorien, kunden, mitarbeiter, ownMaId, isAdmin, t }) {
  const dialog = useDialog();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-based
  const [eintraege, setEintraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKunde, setFilterKunde] = useState('');
  const [filterKategorie, setFilterKategorie] = useState('');
  const [filterBillable, setFilterBillable] = useState('');
  const [drill, setDrill] = useState(null); // { von, bis, kunde_id, kategorie_id, label }

  // #1008 — Scope-Toggle persistiert pro Tab
  const [scope, setScopeState] = useState(() => loadScope('zeiterfassung.scope.monat'));
  const [filterMa, setFilterMaState] = useState(() => loadMaFilter('zeiterfassung.filterMa.monat'));
  const setScope = (v) => { setScopeState(v); saveScope('zeiterfassung.scope.monat', v); };
  const setFilterMa = (v) => { setFilterMaState(v); saveMaFilter('zeiterfassung.filterMa.monat', v); };

  const monthLabel = useMemo(() => {
    const d = new Date(year, month, 1);
    return d.toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
  }, [year, month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = startOfMonth(year, month).toISOString();
      const to = endOfMonth(year, month).toISOString();
      const params = new URLSearchParams({ von: from, bis: to, limit: '2000', scope });
      if (filterKunde) params.set('kunde_id', filterKunde);
      if (filterKategorie) params.set('kategorie_id', filterKategorie);
      if (filterBillable) params.set('billable', filterBillable);
      if (scope === 'team' && filterMa) params.set('mitarbeiter_id', filterMa);
      const res = await fetch(`/api/zeiterfassung/eintraege?${params}`, { credentials: 'include' }).then(r => r.json());
      setEintraege((res.eintraege || []).filter(e => e.end_at));
    } finally {
      setLoading(false);
    }
  }, [year, month, filterKunde, filterKategorie, filterBillable, scope, filterMa]);

  useEffect(() => { load(); }, [load]);

  function prevMonth() {
    const d = new Date(year, month - 1, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  }
  function nextMonth() {
    const d = new Date(year, month + 1, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  }

  // Aggregat: Kunde × Kategorie
  const aggregat = useMemo(() => {
    const map = new Map();
    for (const e of eintraege) {
      const key = `${e.kunde_id || '0'}|${e.kategorie_id || '0'}`;
      if (!map.has(key)) {
        map.set(key, {
          kunde_id: e.kunde_id, kunde_name: e.kunde_name || '–',
          kategorie_id: e.kategorie_id, kategorie_name: e.kategorie_name || '–',
          dauer_min: 0, dauer_billable_min: 0, n: 0,
        });
      }
      const r = map.get(key);
      r.dauer_min += e.dauer_min || 0;
      if (e.billable) r.dauer_billable_min += e.dauer_min || 0;
      r.n += 1;
    }
    return [...map.values()].sort((a, b) => b.dauer_min - a.dauer_min);
  }, [eintraege]);

  const summe = useMemo(() => aggregat.reduce((acc, r) => ({
    dauer_min: acc.dauer_min + r.dauer_min,
    dauer_billable_min: acc.dauer_billable_min + r.dauer_billable_min,
    n: acc.n + r.n,
  }), { dauer_min: 0, dauer_billable_min: 0, n: 0 }), [aggregat]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex items-center gap-1 rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-1">
          <button onClick={prevMonth} className="p-1.5 rounded hover:bg-[var(--bg-inset,#F8F9FC)]"><ChevronLeft className="w-4 h-4" /></button>
          <div className="px-3 py-1 text-[13px] font-medium text-[var(--text-primary)] min-w-[140px] text-center">{monthLabel}</div>
          <button onClick={nextMonth} className="p-1.5 rounded hover:bg-[var(--bg-inset,#F8F9FC)]"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          <FilterRow
            kunden={kunden} kategorien={kategorien}
            filterKunde={filterKunde} setFilterKunde={setFilterKunde}
            filterKategorie={filterKategorie} setFilterKategorie={setFilterKategorie}
            filterBillable={filterBillable} setFilterBillable={setFilterBillable}
            t={t}
          />
          {scope === 'team' && (
            <SearchableSelect
              value={filterMa}
              onChange={setFilterMa}
              placeholder={t('zeiterfassung.scope.maFilterPlaceholder') || 'Mitarbeiter filtern…'}
              size="sm"
              className="min-w-[200px]"
              options={[
                { value: '', label: t('zeiterfassung.scope.alleMitarbeiter') || 'Alle Mitarbeiter' },
                ...mitarbeiter.map(m => ({ value: String(m.id), label: `${m.vorname || ''} ${m.nachname || ''}`.trim() || `MA ${m.id}` })),
              ]}
            />
          )}
          <ScopeToggle scope={scope} onChange={setScope} t={t} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SumCard
          label={t('zeiterfassung.summeMonat') || 'Monat gesamt'}
          subtitle={scope === 'team' ? (t('zeiterfassung.scope.teamSubtitle') || 'Team gesamt') : (t('zeiterfassung.scope.meinSubtitle') || 'Persönlich')}
          value={formatHours(summe.dauer_min)}
        />
        <SumCard
          label={t('zeiterfassung.summeBillable') || 'Davon abrechenbar'}
          subtitle={scope === 'team' ? (t('zeiterfassung.scope.teamSubtitle') || 'Team gesamt') : (t('zeiterfassung.scope.meinSubtitle') || 'Persönlich')}
          value={formatHours(summe.dauer_billable_min)}
          accent="#10B981"
        />
        <SumCard
          label={t('zeiterfassung.eintraege') || 'Einträge'}
          subtitle={scope === 'team' ? (t('zeiterfassung.scope.teamSubtitle') || 'Team gesamt') : (t('zeiterfassung.scope.meinSubtitle') || 'Persönlich')}
          value={`${summe.n}`}
        />
      </div>

      <div className="rounded-[12px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[var(--bg-inset,#F8F9FC)] text-[12px] text-[var(--text-tertiary)] uppercase tracking-wide font-medium">
              <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.kunde') || 'Kunde'}</th>
              <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.kategorie') || 'Kategorie'}</th>
              <th className="text-right px-4 py-2.5 font-medium">{t('zeiterfassung.dauer') || 'Dauer'}</th>
              <th className="text-right px-4 py-2.5 font-medium">{t('zeiterfassung.billable') || 'Abrechnen'}</th>
              <th className="text-right px-4 py-2.5 font-medium">{t('zeiterfassung.eintraege') || 'Einträge'}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="px-4 py-6 text-center text-[var(--text-tertiary)]">{t('common.loading') || 'Lädt …'}</td></tr>}
            {!loading && !aggregat.length && <tr><td colSpan="5" className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('zeiterfassung.keineEintraege') || 'Noch keine Einträge.'}</td></tr>}
            {aggregat.map((r, i) => (
              <tr key={i}
                  onClick={() => setDrill({
                    von: startOfMonth(year, month).toISOString(),
                    bis: endOfMonth(year, month).toISOString(),
                    kunde_id: r.kunde_id || null,
                    kategorie_id: r.kategorie_id || null,
                    label: `${r.kunde_name} · ${r.kategorie_name}`,
                  })}
                  className="border-t border-[var(--border-card,#E5E7EB)] cursor-pointer hover:bg-[var(--bg-inset,#F8F9FC)]">
                <td className="px-4 py-2.5 text-[var(--text-primary)]">{r.kunde_name}</td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.kategorie_name}</td>
                <td className="px-4 py-2.5 text-right text-[var(--text-primary)] font-medium tabular-nums">{formatHours(r.dauer_min)}</td>
                <td className="px-4 py-2.5 text-right text-[#10B981] tabular-nums">{formatHours(r.dauer_billable_min)}</td>
                <td className="px-4 py-2.5 text-right text-[var(--text-secondary)] tabular-nums">{r.n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drill && (
        <EintraegeDrillDownModal
          filters={{ ...drill, scope, mitarbeiter_id: scope === 'team' && filterMa ? filterMa : undefined }}
          kategorien={kategorien}
          kunden={kunden}
          ownMaId={ownMaId}
          isAdmin={isAdmin}
          onClose={() => setDrill(null)}
          onChanged={() => load()}
          t={t}
          dialog={dialog}
          hideMitarbeiter={scope !== 'team'}
        />
      )}
      {dialog.element}
    </div>
  );
}

function FilterRow({ kunden, kategorien, filterKunde, setFilterKunde, filterKategorie, setFilterKategorie, filterBillable, setFilterBillable, t }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <SearchableSelect
        value={filterKunde}
        onChange={setFilterKunde}
        placeholder={t('zeiterfassung.kundeSuchen') || 'Kunde suchen…'}
        size="sm"
        className="min-w-[200px]"
        options={[{ value: '', label: t('zeiterfassung.alleKunden') || 'Alle Kunden' }, ...kunden.map(k => ({ value: String(k.id), label: k.name }))]}
      />
      <SelectMini value={filterKategorie} onChange={setFilterKategorie} placeholder={t('zeiterfassung.alleKategorien') || 'Alle Kategorien'}
        options={[{ value: '', label: t('zeiterfassung.alleKategorien') || 'Alle Kategorien' }, ...kategorien.map(k => ({ value: String(k.id), label: k.name }))]} />
      <SelectMini value={filterBillable} onChange={setFilterBillable} placeholder={t('zeiterfassung.alle') || 'Alle'}
        options={[
          { value: '', label: t('zeiterfassung.alle') || 'Alle' },
          { value: 'true', label: t('zeiterfassung.nurBillable') || 'Nur abrechenbar' },
          { value: 'false', label: t('zeiterfassung.nichtBillable') || 'Nicht abrechenbar' },
        ]} />
    </div>
  );
}

function SelectMini({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px] text-[var(--text-primary)]">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Auswertung (Admin)
// ═══════════════════════════════════════════════════════════════

function AuswertungTab({ kategorien, kunden, t }) {
  const dialog = useDialog();
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [von, setVon] = useState(isoDay(firstOfMonth));
  const [bis, setBis] = useState(isoDay(new Date(today.getFullYear(), today.getMonth() + 1, 1)));
  const [kundeId, setKundeId] = useState('');
  const [maId, setMaId] = useState('');
  const [kategorieId, setKategorieId] = useState('');
  const [billable, setBillable] = useState('');
  const [groupBy, setGroupBy] = useState('kunde_kategorie');
  const [data, setData] = useState({ rows: [], summary: { dauer_min: 0, dauer_billable_min: 0, n_eintraege: 0 } });
  const [loading, setLoading] = useState(false);
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    fetch('/api/mitarbeiter', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setMitarbeiter(Array.isArray(d) ? d : (d.mitarbeiter || [])))
      .catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (von) p.set('von', von);
    if (bis) p.set('bis', bis);
    if (kundeId) p.set('kunde_id', kundeId);
    if (maId) p.set('mitarbeiter_id', maId);
    if (kategorieId) p.set('kategorie_id', kategorieId);
    if (billable) p.set('billable', billable);
    p.set('group_by', groupBy);
    return p;
  }, [von, bis, kundeId, maId, kategorieId, billable, groupBy]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/zeiterfassung/auswertung?${buildParams()}`, { credentials: 'include' }).then(r => r.json());
      setData(res || { rows: [], summary: {} });
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  function downloadCsv() {
    const params = buildParams(); params.set('format', 'csv');
    window.open(`/api/zeiterfassung/auswertung?${params}`, '_blank');
  }

  return (
    <div className="space-y-4">
      <div className="rounded-[12px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[var(--text-tertiary)]" />
          <div className="text-[13px] font-semibold text-[var(--text-primary)]">{t('zeiterfassung.filter') || 'Filter'}</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <Field label={t('zeiterfassung.von') || 'Von'}>
            <input type="date" value={von} onChange={e => setVon(e.target.value)} className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
          </Field>
          <Field label={t('zeiterfassung.bis') || 'Bis'}>
            <input type="date" value={bis} onChange={e => setBis(e.target.value)} className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
          </Field>
          <Field label={t('zeiterfassung.kunde') || 'Kunde'}>
            <SearchableSelect
              value={kundeId}
              onChange={setKundeId}
              placeholder={t('zeiterfassung.kundeSuchen') || 'Kunde suchen…'}
              size="sm"
              options={[{ value: '', label: t('zeiterfassung.alle') || 'Alle' }, ...kunden.map(k => ({ value: String(k.id), label: k.name }))]}
            />
          </Field>
          <Field label={t('zeiterfassung.mitarbeiter') || 'Mitarbeiter'}>
            <SearchableSelect
              value={maId}
              onChange={setMaId}
              placeholder={t('zeiterfassung.mitarbeiterSuchen') || 'Mitarbeiter suchen…'}
              size="sm"
              options={[{ value: '', label: t('zeiterfassung.alle') || 'Alle' }, ...mitarbeiter.map(m => ({ value: String(m.id), label: `${m.vorname || ''} ${m.nachname || ''}`.trim() }))]}
            />
          </Field>
          <Field label={t('zeiterfassung.kategorie') || 'Kategorie'}>
            <SelectFull value={kategorieId} onChange={setKategorieId}
              options={[{ value: '', label: t('zeiterfassung.alle') || 'Alle' }, ...kategorien.map(k => ({ value: String(k.id), label: k.name }))]} />
          </Field>
          <Field label={t('zeiterfassung.billable') || 'Abrechnen'}>
            <SelectFull value={billable} onChange={setBillable}
              options={[
                { value: '', label: t('zeiterfassung.alle') || 'Alle' },
                { value: 'true', label: t('zeiterfassung.nurBillable') || 'Nur abrechenbar' },
                { value: 'false', label: t('zeiterfassung.nichtBillable') || 'Nicht abrechenbar' },
              ]} />
          </Field>
          <Field label={t('zeiterfassung.gruppierung') || 'Gruppierung'}>
            <SelectFull value={groupBy} onChange={setGroupBy} options={[
              { value: 'kunde_kategorie', label: t('zeiterfassung.byKundeKategorie') || 'Kunde × Kategorie' },
              { value: 'kunde', label: t('zeiterfassung.byKunde') || 'Kunde' },
              { value: 'kategorie', label: t('zeiterfassung.byKategorie') || 'Kategorie' },
              { value: 'mitarbeiter', label: t('zeiterfassung.byMitarbeiter') || 'Mitarbeiter' },
            ]} />
          </Field>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={downloadCsv} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] hover:bg-[var(--bg-inset,#F8F9FC)] text-[13px] text-[var(--text-primary)]">
            <Download className="w-3.5 h-3.5" />
            {t('zeiterfassung.csvExport') || 'CSV-Export'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SumCard label={t('zeiterfassung.summeGesamt') || 'Gesamt'} value={formatHours(data.summary?.dauer_min || 0)} />
        <SumCard label={t('zeiterfassung.summeBillable') || 'Davon abrechenbar'} value={formatHours(data.summary?.dauer_billable_min || 0)} accent="#10B981" />
        <SumCard label={t('zeiterfassung.eintraege') || 'Einträge'} value={`${data.summary?.n_eintraege || 0}`} />
      </div>

      <div className="rounded-[12px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[var(--bg-inset,#F8F9FC)] text-[12px] text-[var(--text-tertiary)] uppercase tracking-wide font-medium">
              {(groupBy === 'mitarbeiter') && <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.mitarbeiter') || 'Mitarbeiter'}</th>}
              {(groupBy === 'kunde_kategorie' || groupBy === 'kunde') && <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.kunde') || 'Kunde'}</th>}
              {(groupBy === 'kunde_kategorie' || groupBy === 'kategorie') && <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.kategorie') || 'Kategorie'}</th>}
              <th className="text-right px-4 py-2.5 font-medium">{t('zeiterfassung.dauer') || 'Dauer'}</th>
              <th className="text-right px-4 py-2.5 font-medium">{t('zeiterfassung.billable') || 'Abrechnen'}</th>
              <th className="text-right px-4 py-2.5 font-medium">{t('zeiterfassung.nichtBillable') || 'Nicht abrechenbar'}</th>
              <th className="text-right px-4 py-2.5 font-medium">{t('zeiterfassung.eintraege') || 'Einträge'}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="px-4 py-6 text-center text-[var(--text-tertiary)]">{t('common.loading') || 'Lädt …'}</td></tr>}
            {!loading && !data.rows?.length && <tr><td colSpan="7" className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('zeiterfassung.keineDaten') || 'Keine Daten'}</td></tr>}
            {data.rows?.map((r, i) => (
              <tr key={i}
                  onClick={() => setDrill({
                    von: von ? new Date(von).toISOString() : null,
                    bis: bis ? new Date(bis).toISOString() : null,
                    kunde_id: r.kunde_id ?? null,
                    kategorie_id: r.kategorie_id ?? null,
                    mitarbeiter_id: r.mitarbeiter_id ?? (maId ? parseInt(maId, 10) : null),
                    billable,
                    label: [r.mitarbeiter_name, r.kunde_name, r.kategorie_name].filter(Boolean).join(' · '),
                  })}
                  className="border-t border-[var(--border-card,#E5E7EB)] cursor-pointer hover:bg-[var(--bg-inset,#F8F9FC)]">
                {(groupBy === 'mitarbeiter') && <td className="px-4 py-2.5 text-[var(--text-primary)]">{r.mitarbeiter_name || '–'}</td>}
                {(groupBy === 'kunde_kategorie' || groupBy === 'kunde') && <td className="px-4 py-2.5 text-[var(--text-primary)]">{r.kunde_name || '–'}</td>}
                {(groupBy === 'kunde_kategorie' || groupBy === 'kategorie') && <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.kategorie_name || '–'}</td>}
                <td className="px-4 py-2.5 text-right text-[var(--text-primary)] font-medium tabular-nums">{formatHours(r.dauer_min)}</td>
                <td className="px-4 py-2.5 text-right text-[#10B981] tabular-nums">{formatHours(r.dauer_billable_min)}</td>
                <td className="px-4 py-2.5 text-right text-[var(--text-tertiary)] tabular-nums">{formatHours(r.dauer_non_billable_min)}</td>
                <td className="px-4 py-2.5 text-right text-[var(--text-secondary)] tabular-nums">{r.n_eintraege}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drill && (
        <EintraegeDrillDownModal
          filters={drill}
          kategorien={kategorien}
          kunden={kunden}
          onClose={() => setDrill(null)}
          onChanged={() => load()}
          t={t}
          dialog={dialog}
        />
      )}
      {dialog.element}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-[12px] text-[var(--text-tertiary)]">
      <div className="mb-1 font-medium">{label}</div>
      {children}
    </label>
  );
}

function SelectFull({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px] text-[var(--text-primary)]">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Modals
// ═══════════════════════════════════════════════════════════════

function ModalShell({ title, onClose, children, footer, wide }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-[var(--bg-card,#fff)] rounded-[14px] border border-[var(--border-card,#E5E7EB)] w-full ${wide ? 'max-w-[640px]' : 'max-w-[480px]'} mt-12 shadow-xl`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card,#E5E7EB)]">
          <div className="text-[15px] font-semibold text-[var(--text-primary)]">{title}</div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] rounded-b-[14px]">{footer}</div>}
      </div>
    </div>
  );
}

function TimerStartModal({ kategorien, kunden, onClose, onSubmit, t }) {
  const [kategorieId, setKategorieId] = useState(kategorien[0]?.id ? String(kategorien[0].id) : '');
  const [kundeId, setKundeId] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  // #998 Quickfix: "Abrechenbar" NIE per Default aktiv — User entscheidet bewusst.
  const [billable, setBillable] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handle() {
    if (!kategorieId) return;
    setSubmitting(true);
    await onSubmit({
      kategorie_id: parseInt(kategorieId, 10),
      kunde_id: kundeId ? parseInt(kundeId, 10) : null,
      beschreibung,
      billable,
    });
    setSubmitting(false);
  }

  return (
    <ModalShell
      title={t('zeiterfassung.timerStart') || 'Timer starten'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.cancel') || 'Abbrechen'}</button>
          <button onClick={handle} disabled={!kategorieId || submitting} className="px-4 py-1.5 rounded-[8px] bg-[#338edc] text-white hover:bg-[#1f6fb5] text-[13px] font-medium disabled:opacity-50">{t('zeiterfassung.starten') || 'Starten'}</button>
        </>
      }
    >
      <Field label={t('zeiterfassung.kategorie') || 'Kategorie'}>
        <SelectFull value={kategorieId} onChange={setKategorieId} options={kategorien.map(k => ({ value: String(k.id), label: k.name }))} />
      </Field>
      <Field label={t('zeiterfassung.kunde') || 'Kunde'}>
        <SearchableSelect
          value={kundeId}
          onChange={setKundeId}
          placeholder={t('zeiterfassung.kundeSuchen') || 'Kunde suchen…'}
          size="sm"
          options={[{ value: '', label: t('zeiterfassung.optional') || '— optional —' }, ...kunden.map(k => ({ value: String(k.id), label: k.name }))]}
        />
      </Field>
      <Field label={t('zeiterfassung.beschreibung') || 'Beschreibung'}>
        <input type="text" value={beschreibung} onChange={e => setBeschreibung(e.target.value)}
          className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" placeholder={t('zeiterfassung.optional') || 'Optional'} />
      </Field>
      <label className="flex items-center gap-2 text-[13px] text-[var(--text-primary)] cursor-pointer">
        <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} className="rounded" />
        {t('zeiterfassung.istBillable') || 'Abrechenbar (Kunde verrechnen)'}
      </label>
    </ModalShell>
  );
}

function TimerStopModal({ timer, kategorien, kunden, elapsed, onClose, onSubmit, t }) {
  const [beschreibung, setBeschreibung] = useState(timer?.beschreibung || '');
  const [kategorieId, setKategorieId] = useState(timer?.kategorie_id ? String(timer.kategorie_id) : '');
  const [kundeId, setKundeId] = useState(timer?.kunde_id ? String(timer.kunde_id) : '');
  // #998 Quickfix: "Abrechenbar" NIE per Default aktiv — Stop erzeugt einen neuen Eintrag, User entscheidet bewusst.
  const [billable, setBillable] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handle() {
    setSubmitting(true);
    await onSubmit({
      beschreibung,
      kategorie_id: kategorieId ? parseInt(kategorieId, 10) : undefined,
      kunde_id: kundeId ? parseInt(kundeId, 10) : null,
      billable,
    });
    setSubmitting(false);
  }

  return (
    <ModalShell
      title={t('zeiterfassung.timerStop') || 'Timer stoppen'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.cancel') || 'Abbrechen'}</button>
          <button onClick={handle} disabled={submitting} className="px-4 py-1.5 rounded-[8px] bg-[#EF4444] text-white hover:bg-[#DC2626] text-[13px] font-medium disabled:opacity-50">{t('zeiterfassung.stoppen') || 'Stoppen'}</button>
        </>
      }
    >
      <div className="rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] p-3 flex items-center justify-between">
        <div className="text-[12px] text-[var(--text-tertiary)]">{t('zeiterfassung.dauer') || 'Dauer'}</div>
        <div className="text-[18px] font-bold text-[var(--text-primary)] tabular-nums">{formatTimerSec(elapsed)}</div>
      </div>
      <Field label={t('zeiterfassung.kategorie') || 'Kategorie'}>
        <SelectFull value={kategorieId} onChange={setKategorieId} options={kategorien.map(k => ({ value: String(k.id), label: k.name }))} />
      </Field>
      <Field label={t('zeiterfassung.kunde') || 'Kunde'}>
        <SearchableSelect
          value={kundeId}
          onChange={setKundeId}
          placeholder={t('zeiterfassung.kundeSuchen') || 'Kunde suchen…'}
          size="sm"
          options={[{ value: '', label: t('zeiterfassung.optional') || '— optional —' }, ...kunden.map(k => ({ value: String(k.id), label: k.name }))]}
        />
      </Field>
      <Field label={t('zeiterfassung.beschreibung') || 'Beschreibung'}>
        <textarea value={beschreibung} onChange={e => setBeschreibung(e.target.value)}
          rows={3} className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
      </Field>
      <label className="flex items-center gap-2 text-[13px] text-[var(--text-primary)] cursor-pointer">
        <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} className="rounded" />
        {t('zeiterfassung.istBillable') || 'Abrechenbar (Kunde verrechnen)'}
      </label>
    </ModalShell>
  );
}

// Manueller Eintrag / Edit
function EintragModal({ eintrag, kategorien, kunden, onClose, onSaved, t, dialog, defaultCaseId = null, defaultKundeId = null, defaultBeschreibung = '' }) {
  const isEdit = !!eintrag?.id;
  const today = todayIso();

  const initStart = eintrag?.start_at ? new Date(eintrag.start_at) : null;
  const initEnd = eintrag?.end_at ? new Date(eintrag.end_at) : null;

  const [datum, setDatum] = useState(initStart ? isoDay(initStart) : today);
  const [vonZeit, setVonZeit] = useState(initStart ? `${pad2(initStart.getHours())}:${pad2(initStart.getMinutes())}` : '');
  const [bisZeit, setBisZeit] = useState(initEnd ? `${pad2(initEnd.getHours())}:${pad2(initEnd.getMinutes())}` : '');
  const [dauerMin, setDauerMin] = useState(eintrag?.dauer_min ? String(eintrag.dauer_min) : '');
  const [eingabeArt, setEingabeArt] = useState(initStart && initEnd ? 'zeit' : 'dauer');
  const [kategorieId, setKategorieId] = useState(eintrag?.kategorie_id ? String(eintrag.kategorie_id) : (kategorien[0]?.id ? String(kategorien[0].id) : ''));
  const [kundeId, setKundeId] = useState(
    eintrag?.kunde_id ? String(eintrag.kunde_id) : (defaultKundeId ? String(defaultKundeId) : '')
  );
  // #995 Punkt 0 — case_id durchreichen: bei Edit aus eintrag.case_id, sonst defaultCaseId (z.B. aus Case-Detail)
  const caseIdToPersist = eintrag?.case_id ?? defaultCaseId ?? null;
  const [beschreibung, setBeschreibung] = useState(eintrag?.beschreibung || defaultBeschreibung || '');
  // #998 Quickfix: bei Edit gespeicherten Wert laden, bei NEU immer false (NIE Kategorie-Default übernehmen).
  const [billable, setBillable] = useState(isEdit ? !!eintrag?.billable : false);
  const [submitting, setSubmitting] = useState(false);

  async function handle() {
    if (!kategorieId) {
      await dialog.alert(t('common.error') || 'Fehler', t('zeiterfassung.errKategorie') || 'Kategorie erforderlich', { variant: 'danger' });
      return;
    }
    if (!beschreibung.trim()) {
      await dialog.alert(t('common.error') || 'Fehler', t('zeiterfassung.errBeschreibung') || 'Beschreibung erforderlich', { variant: 'danger' });
      return;
    }

    let startAt = null, endAt = null, dauer = null;
    if (eingabeArt === 'zeit') {
      if (!vonZeit || !bisZeit) {
        await dialog.alert(t('common.error') || 'Fehler', t('zeiterfassung.errZeit') || 'Von und Bis erforderlich', { variant: 'danger' });
        return;
      }
      startAt = new Date(`${datum}T${vonZeit}:00`).toISOString();
      endAt = new Date(`${datum}T${bisZeit}:00`).toISOString();
      if (new Date(endAt) <= new Date(startAt)) {
        await dialog.alert(t('common.error') || 'Fehler', t('zeiterfassung.errBisVorVon') || 'Bis muss nach Von liegen', { variant: 'danger' });
        return;
      }
    } else {
      const min = parseInt(dauerMin, 10);
      if (!min || min < 1) {
        await dialog.alert(t('common.error') || 'Fehler', t('zeiterfassung.errDauer') || 'Dauer in Minuten erforderlich', { variant: 'danger' });
        return;
      }
      // Set start = 00:00 of date, end = +dauer (für Sortierung; Zeitslot wird sonst nicht genutzt)
      const start = new Date(`${datum}T08:00:00`);
      const end = new Date(start.getTime() + min * 60000);
      startAt = start.toISOString();
      endAt = end.toISOString();
      dauer = min;
    }

    const body = {
      kategorie_id: parseInt(kategorieId, 10),
      kunde_id: kundeId ? parseInt(kundeId, 10) : null,
      start_at: startAt,
      end_at: endAt,
      beschreibung,
      billable,
    };
    if (dauer != null) body.dauer_min = dauer;
    // #995 Punkt 0 — case_id mitschicken (Edit: preserve, Neu im Case-Modal: pre-fill)
    if (caseIdToPersist != null) body.case_id = parseInt(caseIdToPersist, 10);

    setSubmitting(true);
    try {
      const res = await fetch(
        isEdit ? `/api/zeiterfassung/eintraege/${eintrag.id}` : '/api/zeiterfassung/eintraege',
        {
          method: isEdit ? 'PUT' : 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        await dialog.alert(t('common.error') || 'Fehler', d.error || 'Speichern fehlgeschlagen', { variant: 'danger' });
        return;
      }
      onSaved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title={isEdit ? (t('zeiterfassung.eintragBearbeiten') || 'Eintrag bearbeiten') : (t('zeiterfassung.neuerEintrag') || 'Neuer Eintrag')}
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.cancel') || 'Abbrechen'}</button>
          <button onClick={handle} disabled={submitting} className="px-4 py-1.5 rounded-[8px] bg-[#338edc] text-white hover:bg-[#1f6fb5] text-[13px] font-medium disabled:opacity-50">{t('common.save') || 'Speichern'}</button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <Field label={t('zeiterfassung.datum') || 'Datum'}>
          <input type="date" value={datum} onChange={e => setDatum(e.target.value)}
            className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
        </Field>
        <Field label={t('zeiterfassung.eingabeArt') || 'Eingabe'}>
          <SelectFull value={eingabeArt} onChange={setEingabeArt} options={[
            { value: 'zeit', label: t('zeiterfassung.eingabeZeit') || 'Von – Bis Uhrzeit' },
            { value: 'dauer', label: t('zeiterfassung.eingabeDauer') || 'Nur Dauer (Minuten)' },
          ]} />
        </Field>
      </div>

      {eingabeArt === 'zeit' ? (
        <div className="grid grid-cols-2 gap-2">
          <Field label={t('zeiterfassung.von') || 'Von'}>
            <input type="time" value={vonZeit} onChange={e => setVonZeit(e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
          </Field>
          <Field label={t('zeiterfassung.bis') || 'Bis'}>
            <input type="time" value={bisZeit} onChange={e => setBisZeit(e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
          </Field>
        </div>
      ) : (
        <Field label={t('zeiterfassung.dauerMin') || 'Dauer (Minuten)'}>
          <input type="number" min="1" value={dauerMin} onChange={e => setDauerMin(e.target.value)}
            className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
        </Field>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Field label={t('zeiterfassung.kategorie') || 'Kategorie'}>
          <SelectFull value={kategorieId} onChange={setKategorieId} options={kategorien.map(k => ({ value: String(k.id), label: k.name }))} />
        </Field>
        <Field label={t('zeiterfassung.kunde') || 'Kunde'}>
          <SearchableSelect
            value={kundeId}
            onChange={setKundeId}
            placeholder={t('zeiterfassung.kundeSuchen') || 'Kunde suchen…'}
            size="sm"
            options={[{ value: '', label: t('zeiterfassung.optional') || '— optional —' }, ...kunden.map(k => ({ value: String(k.id), label: k.name }))]}
          />
        </Field>
      </div>

      <Field label={t('zeiterfassung.beschreibung') || 'Beschreibung'}>
        <textarea value={beschreibung} onChange={e => setBeschreibung(e.target.value)}
          rows={3} className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
      </Field>

      <label className="flex items-center gap-2 text-[13px] text-[var(--text-primary)] cursor-pointer">
        <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} className="rounded" />
        {t('zeiterfassung.istBillable') || 'Abrechenbar (Kunde verrechnen)'}
      </label>
    </ModalShell>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Drill-Down Modal (P4)
// ═══════════════════════════════════════════════════════════════

function EintraegeDrillDownModal({ filters, kategorien, kunden, onClose, onChanged, t, dialog, hideMitarbeiter = false, ownMaId = null, isAdmin = false }) {
  const [eintraege, setEintraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEntry, setEditEntry] = useState(null);

  // #995 P4b — AI-Summary
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCached, setAiCached] = useState(false);
  const [aiGeneratedAt, setAiGeneratedAt] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.von) p.set('von', filters.von);
      if (filters.bis) p.set('bis', filters.bis);
      if (filters.kunde_id != null) p.set('kunde_id', String(filters.kunde_id));
      if (filters.kategorie_id != null) p.set('kategorie_id', String(filters.kategorie_id));
      if (filters.mitarbeiter_id != null) p.set('mitarbeiter_id', String(filters.mitarbeiter_id));
      if (filters.billable) p.set('billable', filters.billable);
      if (filters.scope) p.set('scope', filters.scope);
      p.set('limit', '500');
      const r = await fetch(`/api/zeiterfassung/eintraege?${p}`, { credentials: 'include' }).then(r => r.json());
      setEintraege((r.eintraege || []).filter(e => e.end_at));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadSummary = useCallback(async (refresh = false) => {
    setAiLoading(true);
    setAiError(null);
    try {
      const p = new URLSearchParams();
      if (filters.von) p.set('von', filters.von);
      if (filters.bis) p.set('bis', filters.bis);
      if (filters.kunde_id != null) p.set('kunde_id', String(filters.kunde_id));
      if (filters.kategorie_id != null) p.set('kategorie_id', String(filters.kategorie_id));
      if (filters.mitarbeiter_id != null) p.set('mitarbeiter_id', String(filters.mitarbeiter_id));
      if (filters.billable) p.set('billable', filters.billable);
      if (refresh) p.set('refresh', '1');
      const res = await fetch(`/api/zeiterfassung/summary?${p}`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || `HTTP ${res.status}`);
      }
      const r = await res.json();
      setAiSummary(r.summary || '');
      setAiCached(!!r.cached);
      setAiGeneratedAt(r.generated_at || null);
    } catch (err) {
      setAiError(err.message || 'Fehler');
    } finally {
      setAiLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id) {
    const ok = await dialog.confirm(
      t('zeiterfassung.deleteTitle') || 'Eintrag löschen?',
      t('zeiterfassung.deleteMessage') || 'Soll dieser Eintrag dauerhaft gelöscht werden?',
      { variant: 'danger', confirmLabel: t('common.delete') || 'Löschen' }
    );
    if (!ok) return;
    const res = await fetch(`/api/zeiterfassung/eintraege/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      await dialog.alert(t('common.error') || 'Fehler', d.error || 'Löschen fehlgeschlagen', { variant: 'danger' });
      return;
    }
    await load();
    onChanged && onChanged();
  }

  const summe = eintraege.reduce((acc, e) => ({
    dauer_min: acc.dauer_min + (e.dauer_min || 0),
    dauer_billable_min: acc.dauer_billable_min + (e.billable ? (e.dauer_min || 0) : 0),
  }), { dauer_min: 0, dauer_billable_min: 0 });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[var(--bg-card,#fff)] rounded-[14px] border border-[var(--border-card,#E5E7EB)] w-[95%] max-w-[1600px] mt-12 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card,#E5E7EB)]">
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-[var(--text-primary)]">{t('zeiterfassung.detailView') || 'Detail-Ansicht'}</div>
            <div className="text-[12px] text-[var(--text-tertiary)] truncate">{filters.label || ''}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-3 grid grid-cols-3 gap-2 border-b border-[var(--border-card,#E5E7EB)]">
          <SumCard label={t('zeiterfassung.summeGesamt') || 'Gesamt'} value={formatHours(summe.dauer_min)} />
          <SumCard label={t('zeiterfassung.summeBillable') || 'Davon abrechenbar'} value={formatHours(summe.dauer_billable_min)} accent="#10B981" />
          <SumCard label={t('zeiterfassung.eintraege') || 'Einträge'} value={`${eintraege.length}`} />
        </div>

        {/* #995 P4b — AI-Summary */}
        <div className="px-5 py-3 border-b border-[var(--border-card,#E5E7EB)]">
          {!aiOpen && !aiSummary && (
            <button
              onClick={() => { setAiOpen(true); if (!aiSummary) loadSummary(false); }}
              disabled={!eintraege.length}
              className="px-3 py-1.5 rounded-[8px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-[12px] text-[var(--text-primary)] hover:bg-[#338edc]/5 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              <span>✨</span>{t('zeiterfassung.aiSummaryShow') || 'KI-Zusammenfassung anzeigen'}
            </button>
          )}
          {(aiOpen || aiSummary) && (
            <div className="rounded-[10px] border border-[#338edc]/20 bg-[#338edc]/5 p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--text-primary)]">
                  <span>✨</span><span>{t('zeiterfassung.aiSummary') || 'KI-Zusammenfassung'}</span>
                  {aiCached && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] text-[var(--text-tertiary)]" title={t('zeiterfassung.aiCachedTooltip') || 'Aus dem Cache geladen — Daten unverändert seit Erzeugung'}>
                      {t('zeiterfassung.aiCached') || 'aus Cache'}
                    </span>
                  )}
                  {aiGeneratedAt && (
                    <span className="text-[10px] text-[var(--text-tertiary)]">{new Date(aiGeneratedAt).toLocaleString('de-DE')}</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => loadSummary(true)}
                    disabled={aiLoading}
                    className="px-2 py-1 rounded-[6px] bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] text-[11px] text-[var(--text-primary)] hover:bg-[#338edc]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={t('zeiterfassung.aiRefresh') || 'Neu generieren'}
                  >
                    {aiLoading ? (t('common.loading') || 'Lädt …') : (t('zeiterfassung.aiRefresh') || '↻ Neu')}
                  </button>
                  <button
                    onClick={() => { setAiOpen(false); setAiSummary(null); setAiError(null); }}
                    className="p-1 rounded hover:bg-[var(--bg-card,#fff)] text-[var(--text-tertiary)]"
                    title={t('common.close') || 'Schließen'}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {aiLoading && !aiSummary && (
                <div className="text-[12px] text-[var(--text-tertiary)] py-2">{t('zeiterfassung.aiGenerating') || 'KI generiert Zusammenfassung …'}</div>
              )}
              {aiError && (
                <div className="text-[12px] text-[#EF4444] py-2">{t('common.error') || 'Fehler'}: {aiError}</div>
              )}
              {aiSummary && (
                <div className="text-[13px] text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap">{aiSummary}</div>
              )}
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)] text-[12px] text-[var(--text-tertiary)] uppercase tracking-wide font-medium">
                <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.datum') || 'Datum'}</th>
                <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.zeit') || 'Zeit'}</th>
                {!hideMitarbeiter && <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.mitarbeiter') || 'Mitarbeiter'}</th>}
                <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.kategorie') || 'Kategorie'}</th>
                <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.kunde') || 'Kunde'}</th>
                <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.beschreibung') || 'Beschreibung'}</th>
                <th className="text-right px-4 py-2.5 font-medium">{t('zeiterfassung.dauer') || 'Dauer'}</th>
                <th className="text-center px-4 py-2.5 font-medium">{t('zeiterfassung.billable') || 'Abrechnen'}</th>
                <th className="text-right px-4 py-2.5 font-medium">{t('common.actions') || 'Aktionen'}</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={hideMitarbeiter ? 8 : 9} className="px-4 py-6 text-center text-[var(--text-tertiary)]">{t('common.loading') || 'Lädt …'}</td></tr>}
              {!loading && !eintraege.length && <tr><td colSpan={hideMitarbeiter ? 8 : 9} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('zeiterfassung.keineEintraege') || 'Keine Einträge.'}</td></tr>}
              {eintraege.map(e => {
                const canMutate = !!isAdmin || (ownMaId != null && e.mitarbeiter_id === ownMaId);
                const fremdTitle = canMutate ? '' : (t('zeiterfassung.scope.fremdHint') || 'Nur eigene Einträge bearbeiten');
                const maName = (e.ma_vorname || e.ma_nachname) ? `${e.ma_vorname || ''} ${e.ma_nachname || ''}`.trim() : (e.mitarbeiter_name || '—');
                return (
                <tr key={e.id} className="border-t border-[var(--border-card,#E5E7EB)]">
                  <td className="px-4 py-2.5 text-[var(--text-secondary)]">{e.start_at ? new Date(e.start_at).toLocaleDateString('de-DE') : '—'}</td>
                  <td className="px-4 py-2.5 text-[var(--text-secondary)] tabular-nums">{e.start_at ? `${fmtTime(e.start_at)}–${fmtTime(e.end_at)}` : '—'}</td>
                  {!hideMitarbeiter && <td className="px-4 py-2.5 text-[var(--text-secondary)]">{maName}</td>}
                  <td className="px-4 py-2.5 text-[var(--text-primary)]">{e.kategorie_name || '—'}</td>
                  <td className="px-4 py-2.5 text-[var(--text-secondary)]">{e.kunde_name || '–'}</td>
                  <td className="px-4 py-2.5 text-[var(--text-secondary)] max-w-[280px] truncate" title={e.beschreibung}>{e.beschreibung || '—'}</td>
                  <td className="px-4 py-2.5 text-right text-[var(--text-primary)] font-medium tabular-nums">{formatMin(e.dauer_min)}</td>
                  <td className="px-4 py-2.5 text-center">
                    {e.billable ? <span className="inline-block px-2 py-0.5 rounded-[6px] bg-[#10B981]/10 text-[#10B981] text-[11px] font-medium">€</span> : <span className="text-[var(--text-tertiary)] text-[11px]">–</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => canMutate && setEditEntry(e)}
                        disabled={!canMutate}
                        className="p-1.5 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        title={canMutate ? (t('common.edit') || 'Bearbeiten') : fremdTitle}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => canMutate && handleDelete(e.id)}
                        disabled={!canMutate}
                        className="p-1.5 rounded hover:bg-[#EF4444]/10 text-[#EF4444] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        title={canMutate ? (t('common.delete') || 'Löschen') : fremdTitle}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end px-5 py-3 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] rounded-b-[14px]">
          <button onClick={onClose} className="px-4 py-1.5 rounded-[8px] bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] text-[13px] text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.close') || 'Schließen'}</button>
        </div>
      </div>

      {editEntry && (
        <EintragModal
          eintrag={editEntry}
          kategorien={kategorien}
          kunden={kunden}
          onClose={() => setEditEntry(null)}
          onSaved={() => { setEditEntry(null); load(); onChanged && onChanged(); }}
          t={t}
          dialog={dialog}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Einstellungen / Kategorien-Editor (P3)
// ═══════════════════════════════════════════════════════════════

function defaultCategoryColor(idx) {
  const palette = ['#338edc', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
  return palette[idx % palette.length];
}

function EinstellungenTab({ t, dialog, onCategoriesChanged }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/zeiterfassung/kategorien?include_inaktiv=1', { credentials: 'include' }).then(r => r.json());
      setList(r.kategorien || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function move(idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[idx], next[target]] = [next[target], next[idx]];
    setList(next);
    const ids = next.map(k => k.id);
    await fetch('/api/zeiterfassung/kategorien/reorder', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    onCategoriesChanged && onCategoriesChanged();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[16px] font-semibold text-[var(--text-primary)]">{t('zeiterfassung.kategorienVerwalten') || 'Kategorien verwalten'}</h2>
          <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">{t('zeiterfassung.kategorienHinweis') || 'Reihenfolge, Farbe, Default-Billable. Soft-Delete deaktiviert nur, harter Delete bei Verwendung erfordert Reassign.'}</p>
        </div>
        <button onClick={() => setEditing({ neu: true })} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] bg-[#338edc] text-white hover:bg-[#1f6fb5] text-[13px] font-medium">
          <Plus className="w-4 h-4" />
          {t('zeiterfassung.kategorieNeu') || 'Neue Kategorie'}
        </button>
      </div>

      <div className="rounded-[12px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[var(--bg-inset,#F8F9FC)] text-[12px] text-[var(--text-tertiary)] uppercase tracking-wide font-medium">
              <th className="text-left px-4 py-2.5 font-medium w-[80px]">{t('zeiterfassung.reihenfolge') || 'Reihen.'}</th>
              <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.farbe') || 'Farbe'}</th>
              <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.name') || 'Name'}</th>
              <th className="text-left px-4 py-2.5 font-medium">{t('zeiterfassung.beschreibung') || 'Beschreibung'}</th>
              <th className="text-center px-4 py-2.5 font-medium">{t('zeiterfassung.defaultBillable') || 'Default Abrechen'}</th>
              <th className="text-center px-4 py-2.5 font-medium">{t('zeiterfassung.aktiv') || 'Aktiv'}</th>
              <th className="text-right px-4 py-2.5 font-medium">{t('common.actions') || 'Aktionen'}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="px-4 py-6 text-center text-[var(--text-tertiary)]">{t('common.loading') || 'Lädt …'}</td></tr>}
            {!loading && list.map((k, i) => (
              <tr key={k.id} className="border-t border-[var(--border-card,#E5E7EB)]">
                <td className="px-4 py-2.5">
                  <div className="inline-flex gap-0.5">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)] disabled:opacity-30">↑</button>
                    <button onClick={() => move(i, 1)} disabled={i === list.length - 1} className="p-1 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)] disabled:opacity-30">↓</button>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <div className="w-5 h-5 rounded-full border border-[var(--border-card,#E5E7EB)]" style={{ background: k.farbe || defaultCategoryColor(i) }} />
                </td>
                <td className="px-4 py-2.5 text-[var(--text-primary)] font-medium">{k.name}</td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)] max-w-[300px] truncate" title={k.beschreibung}>{k.beschreibung || '—'}</td>
                <td className="px-4 py-2.5 text-center">
                  {k.default_billable ? <span className="inline-block px-2 py-0.5 rounded-[6px] bg-[#10B981]/10 text-[#10B981] text-[11px] font-medium">€</span> : <span className="text-[var(--text-tertiary)] text-[11px]">–</span>}
                </td>
                <td className="px-4 py-2.5 text-center">
                  {k.aktiv ? <span className="inline-block px-2 py-0.5 rounded-[6px] bg-[#338edc]/10 text-[#338edc] text-[11px] font-medium">{t('zeiterfassung.aktiv') || 'aktiv'}</span> : <span className="inline-block px-2 py-0.5 rounded-[6px] bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)] text-[11px]">{t('zeiterfassung.inaktiv') || 'inaktiv'}</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setEditing(k)} className="p-1.5 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]" title={t('common.edit') || 'Bearbeiten'}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setDeleting(k)} className="p-1.5 rounded hover:bg-[#EF4444]/10 text-[#EF4444]" title={t('common.delete') || 'Löschen'}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <KategorieModal
          kategorie={editing.neu ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onCategoriesChanged && onCategoriesChanged(); }}
          t={t}
          dialog={dialog}
        />
      )}
      {deleting && (
        <KategorieDeleteModal
          kategorie={deleting}
          alleKategorien={list}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); load(); onCategoriesChanged && onCategoriesChanged(); }}
          t={t}
          dialog={dialog}
        />
      )}
    </div>
  );
}

function KategorieModal({ kategorie, onClose, onSaved, t, dialog }) {
  const isEdit = !!kategorie?.id;
  const [name, setName] = useState(kategorie?.name || '');
  const [beschreibung, setBeschreibung] = useState(kategorie?.beschreibung || '');
  const [defaultBillable, setDefaultBillable] = useState(!!kategorie?.default_billable);
  const [farbe, setFarbe] = useState(kategorie?.farbe || '#338edc');
  const [aktiv, setAktiv] = useState(kategorie?.aktiv !== false);
  const [submitting, setSubmitting] = useState(false);

  async function handle() {
    if (!name.trim()) {
      await dialog.alert(t('common.error') || 'Fehler', t('zeiterfassung.errName') || 'Name erforderlich', { variant: 'danger' });
      return;
    }
    setSubmitting(true);
    try {
      const body = { name: name.trim(), beschreibung: beschreibung.trim() || null, default_billable: defaultBillable, farbe };
      if (isEdit) body.aktiv = aktiv;
      const res = await fetch(
        isEdit ? `/api/zeiterfassung/kategorien/${kategorie.id}` : '/api/zeiterfassung/kategorien',
        {
          method: isEdit ? 'PUT' : 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        await dialog.alert(t('common.error') || 'Fehler', d.error || 'Speichern fehlgeschlagen', { variant: 'danger' });
        return;
      }
      onSaved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title={isEdit ? (t('zeiterfassung.kategorieBearbeiten') || 'Kategorie bearbeiten') : (t('zeiterfassung.kategorieNeu') || 'Neue Kategorie')}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.cancel') || 'Abbrechen'}</button>
          <button onClick={handle} disabled={submitting} className="px-4 py-1.5 rounded-[8px] bg-[#338edc] text-white hover:bg-[#1f6fb5] text-[13px] font-medium disabled:opacity-50">{t('common.save') || 'Speichern'}</button>
        </>
      }
    >
      <Field label={t('zeiterfassung.name') || 'Name'}>
        <input type="text" value={name} onChange={e => setName(e.target.value)}
          className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
      </Field>
      <Field label={t('zeiterfassung.beschreibung') || 'Beschreibung'}>
        <input type="text" value={beschreibung} onChange={e => setBeschreibung(e.target.value)}
          className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px]" />
      </Field>
      <Field label={t('zeiterfassung.farbe') || 'Farbe'}>
        <div className="flex items-center gap-2">
          <input type="color" value={farbe} onChange={e => setFarbe(e.target.value)} className="w-12 h-8 rounded border border-[var(--border-card,#E5E7EB)] cursor-pointer" />
          <input type="text" value={farbe} onChange={e => setFarbe(e.target.value)} placeholder="#338edc"
            className="flex-1 px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[13px] tabular-nums" />
        </div>
      </Field>
      <label className="flex items-center gap-2 text-[13px] text-[var(--text-primary)] cursor-pointer">
        <input type="checkbox" checked={defaultBillable} onChange={e => setDefaultBillable(e.target.checked)} className="rounded" />
        {t('zeiterfassung.defaultBillable') || 'Default abrechenbar'}
      </label>
      {isEdit && (
        <label className="flex items-center gap-2 text-[13px] text-[var(--text-primary)] cursor-pointer">
          <input type="checkbox" checked={aktiv} onChange={e => setAktiv(e.target.checked)} className="rounded" />
          {t('zeiterfassung.aktiv') || 'Aktiv'}
        </label>
      )}
    </ModalShell>
  );
}

function KategorieDeleteModal({ kategorie, alleKategorien, onClose, onDone, t, dialog }) {
  const [usage, setUsage] = useState(null);
  const [mode, setMode] = useState('soft');
  const [reassignTo, setReassignTo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`/api/zeiterfassung/kategorien/${kategorie.id}/usage`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setUsage({ count: d?.usage ?? 0 }))
      .catch(() => setUsage({ count: 0 }));
  }, [kategorie.id]);

  const inUse = (usage?.count || 0) > 0;
  const reassignOptions = alleKategorien.filter(k => k.id !== kategorie.id && k.aktiv).map(k => ({ value: String(k.id), label: k.name }));

  async function handle() {
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      const body = {};
      if (inUse) {
        params.set('mode', mode);
        if (mode === 'reassign') {
          if (!reassignTo) {
            await dialog.alert(t('common.error') || 'Fehler', t('zeiterfassung.errReassign') || 'Ziel-Kategorie erforderlich', { variant: 'danger' });
            return;
          }
          body.reassign_to = parseInt(reassignTo, 10);
        }
      }
      const url = `/api/zeiterfassung/kategorien/${kategorie.id}${params.toString() ? '?' + params : ''}`;
      const hasBody = Object.keys(body).length > 0;
      const res = await fetch(url, {
        method: 'DELETE', credentials: 'include',
        ...(hasBody ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        await dialog.alert(t('common.error') || 'Fehler', d.error || 'Löschen fehlgeschlagen', { variant: 'danger' });
        return;
      }
      onDone();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title={t('zeiterfassung.kategorieLoeschen') || 'Kategorie löschen'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.cancel') || 'Abbrechen'}</button>
          <button onClick={handle} disabled={submitting || (inUse && mode === 'reassign' && !reassignTo)} className="px-4 py-1.5 rounded-[8px] bg-[#EF4444] text-white hover:bg-[#DC2626] text-[13px] font-medium disabled:opacity-50">{t('common.delete') || 'Löschen'}</button>
        </>
      }
    >
      <div className="text-[13px] text-[var(--text-primary)]">
        <span className="font-medium">{kategorie.name}</span>
        {usage && <span className="text-[var(--text-tertiary)]"> · {usage.count || 0} {t('zeiterfassung.eintraege') || 'Einträge'}</span>}
      </div>

      {!inUse ? (
        <div className="rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] p-3 text-[13px] text-[var(--text-secondary)]">
          {t('zeiterfassung.deleteUnusedHint') || 'Diese Kategorie wird nicht verwendet und kann sofort gelöscht werden.'}
        </div>
      ) : (
        <>
          <Field label={t('zeiterfassung.deleteMode') || 'Modus'}>
            <SelectFull value={mode} onChange={setMode} options={[
              { value: 'soft', label: t('zeiterfassung.deleteSoft') || 'Soft-Delete (deaktivieren, Einträge bleiben)' },
              { value: 'reassign', label: t('zeiterfassung.deleteReassign') || 'Reassign + Hard-Delete (Einträge auf andere Kategorie verschieben)' },
            ]} />
          </Field>
          {mode === 'reassign' && (
            <Field label={t('zeiterfassung.reassignTo') || 'Ziel-Kategorie'}>
              <SelectFull value={reassignTo} onChange={setReassignTo}
                options={[{ value: '', label: t('zeiterfassung.bitteWaehlen') || '— bitte wählen —' }, ...reassignOptions]} />
            </Field>
          )}
        </>
      )}
    </ModalShell>
  );
}

// ═══════════════════════════════════════════════════════════════
//  Wochenansicht Google-Calendar-Stil (P2)
// ═══════════════════════════════════════════════════════════════

const WEEK_HOUR_START = 6;
const WEEK_HOUR_END = 22;
const WEEK_HOUR_HEIGHT = 40;

function getISOWeek(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}

function WochenKalender({ kategorien, eintraege, weekStart, onPrev, onNext, onToday, onEdit, t }) {
  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [weekStart]);

  const hours = useMemo(() => {
    const arr = [];
    for (let h = WEEK_HOUR_START; h <= WEEK_HOUR_END; h++) arr.push(h);
    return arr;
  }, []);

  const totalHeight = (WEEK_HOUR_END - WEEK_HOUR_START) * WEEK_HOUR_HEIGHT;

  function eintraegeForDay(d) {
    const dayStart = new Date(d); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
    return eintraege.filter(e => {
      if (!e.start_at || !e.end_at) return false;
      const s = new Date(e.start_at);
      return s >= dayStart && s < dayEnd;
    });
  }

  function blockStyle(e) {
    const s = new Date(e.start_at);
    const en = new Date(e.end_at);
    const startMin = s.getHours() * 60 + s.getMinutes();
    const endMin = en.getHours() * 60 + en.getMinutes();
    const top = ((startMin - WEEK_HOUR_START * 60) / 60) * WEEK_HOUR_HEIGHT;
    const height = Math.max(18, ((endMin - startMin) / 60) * WEEK_HOUR_HEIGHT);
    const kat = kategorien.find(k => k.id === e.kategorie_id);
    const idx = kategorien.findIndex(k => k.id === e.kategorie_id);
    const color = kat?.farbe || defaultCategoryColor(idx >= 0 ? idx : 0);
    return { top, height, color };
  }

  const todayIsoStr = isoDay(new Date());
  const dayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1 rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-1">
          <button onClick={onPrev} className="p-1.5 rounded hover:bg-[var(--bg-inset,#F8F9FC)]"><ChevronLeft className="w-4 h-4" /></button>
          <div className="px-3 py-1 text-[13px] font-medium text-[var(--text-primary)]">
            {t('zeiterfassung.kw') || 'KW'} {getISOWeek(weekStart)} · {weekStart.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – {days[6].toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
          </div>
          <button onClick={onNext} className="p-1.5 rounded hover:bg-[var(--bg-inset,#F8F9FC)]"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <button onClick={onToday} className="px-3 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] hover:bg-[var(--bg-inset,#F8F9FC)] text-[13px] text-[var(--text-primary)]">
          {t('zeiterfassung.heute') || 'Heute'}
        </button>
      </div>

      <div className="rounded-[12px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
        <div className="grid" style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}>
          <div className="border-b border-r border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] h-[44px]"></div>
          {days.map((d, i) => {
            const isToday = isoDay(d) === todayIsoStr;
            return (
              <div key={i} className={`border-b border-r border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] h-[44px] flex flex-col items-center justify-center ${isToday ? 'bg-[#338edc]/5' : ''}`}>
                <div className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary)] font-medium">{dayLabels[i]}</div>
                <div className={`text-[14px] font-semibold tabular-nums ${isToday ? 'text-[#338edc]' : 'text-[var(--text-primary)]'}`}>{d.getDate()}.{pad2(d.getMonth() + 1)}</div>
              </div>
            );
          })}
        </div>

        <div className="grid relative" style={{ gridTemplateColumns: '60px repeat(7, 1fr)', height: totalHeight }}>
          <div className="border-r border-[var(--border-card,#E5E7EB)] relative">
            {hours.map(h => (
              <div key={h} className="absolute left-0 right-0 text-[10px] text-[var(--text-tertiary)] text-right pr-1.5 -translate-y-1/2 tabular-nums" style={{ top: (h - WEEK_HOUR_START) * WEEK_HOUR_HEIGHT }}>
                {pad2(h)}:00
              </div>
            ))}
          </div>

          {days.map((d, di) => {
            const dayEintraege = eintraegeForDay(d);
            return (
              <div key={di} className="border-r border-[var(--border-card,#E5E7EB)] relative">
                {hours.map(h => (
                  <div key={h} className="absolute left-0 right-0 border-t border-[var(--border-card,#E5E7EB)]" style={{ top: (h - WEEK_HOUR_START) * WEEK_HOUR_HEIGHT, height: WEEK_HOUR_HEIGHT }}></div>
                ))}
                {dayEintraege.map(e => {
                  const st = blockStyle(e);
                  return (
                    <div
                      key={e.id}
                      onClick={() => onEdit && onEdit(e)}
                      className="absolute left-0.5 right-0.5 rounded-[6px] px-1.5 py-1 text-[11px] text-white cursor-pointer overflow-hidden hover:opacity-90 shadow-sm"
                      style={{ top: st.top, height: st.height, background: st.color }}
                      title={`${e.kategorie_name || ''}${e.kunde_name ? ' · ' + e.kunde_name : ''}\n${fmtTime(e.start_at)}–${fmtTime(e.end_at)}\n${e.beschreibung || ''}`}
                    >
                      <div className="font-medium truncate leading-tight">{e.kategorie_name || '—'}</div>
                      {st.height > 28 && <div className="opacity-90 truncate leading-tight tabular-nums">{fmtTime(e.start_at)}–{fmtTime(e.end_at)}</div>}
                      {st.height > 44 && e.beschreibung && <div className="opacity-80 truncate leading-tight">{e.beschreibung}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
